import React, { useEffect, useState } from 'react';
import { Modal, Spin, Tabs, Empty } from 'antd';
import { Eye } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { REACTIONS, reactionSummary, type Me, type Post } from '../socialStore';
import { feedApi, type Reactor, type Viewer } from '../socialApi';

export type EngagementTab = 'all' | 'views' | string;

function Row({ name, avatar, isMe, badge }: { name: string; avatar: string | null; isMe: boolean; badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="relative">
        <UserAvatar name={name} src={avatar} size={36} />
        {badge && <span className="absolute -bottom-1 -right-1 text-sm leading-none">{badge}</span>}
      </div>
      <span className="flex-1 text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{name}{isMe && <span className="font-normal text-gray-400"> (Bạn)</span>}</span>
    </div>
  );
}

/** Ai đã thả cảm xúc (theo từng loại) và ai đã xem bài. Tải từ API khi mở hộp */
export function EngagementModal({ post, me, open, initialTab, onClose }: { post: Post; me: Me; open: boolean; initialTab: EngagementTab; onClose: () => void }) {
  const [tab, setTab] = useState<EngagementTab>(initialTab);
  const { total, top } = reactionSummary(post, me.id);
  const [reactors, setReactors] = useState<Reactor[] | null>(null);
  const [viewers, setViewers] = useState<{ list: Viewer[]; earlier: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const label = (emoji: string) => REACTIONS.find(r => r.emoji === emoji)?.label || emoji;

  useEffect(() => {
    let live = true;
    Promise.all([feedApi.reactors(post.id), feedApi.viewers(post.id)])
      .then(([r, v]) => { if (live) { setReactors(r); setViewers(v); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [post.id]);

  const status = failed
    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa tải được danh sách, hãy thử lại sau." />
    : <div className="py-8 text-center"><Spin /></div>;

  const reactionList = (emoji?: string) => {
    if (!reactors) return status;
    const list = reactors.filter(r => !emoji || r.emoji === emoji);
    return list.length
      ? <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">{list.map(r => <Row key={r.userId} name={r.name} avatar={r.avatar} isMe={r.userId === me.id} badge={r.emoji} />)}</div>
      : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ai thả cảm xúc" />;
  };

  const items = [
    { key: 'all', label: `Tất cả ${total}`, children: reactionList() },
    ...top.map(t => ({ key: t.emoji, label: <span title={label(t.emoji)}>{t.emoji} {t.count}</span>, children: reactionList(t.emoji) })),
    {
      key: 'views',
      label: <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Đã xem {post.views}</span>,
      children: !viewers ? status : (
        <div className="max-h-[50vh] overflow-y-auto">
          {viewers.list.length > 0 && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {viewers.list.map(v => <Row key={v.userId} name={v.name} avatar={v.avatar} isMe={v.userId === me.id} />)}
            </div>
          )}
          {viewers.earlier > 0 && (
            <div className="text-xs text-gray-500 mt-2">và {viewers.earlier} người xem khác.</div>
          )}
          {!viewers.list.length && !viewers.earlier && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có ai xem" />}
        </div>
      ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Tương tác với bài viết" width={440} destroyOnHidden>
      <Tabs activeKey={items.some(i => i.key === tab) ? tab : 'all'} onChange={setTab} items={items} size="small" />
    </Modal>
  );
}
